import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ApplicationMessage,
  messageState,
} from "@mrwa/rime-replacement/esriControls/general/ApplicationMessage";
import axios from "axios";
import React from "react";
import { getCorpAuthToken } from "../Auth";
import { CrashContext } from "../context";
import NomadBreadcrumbs from "./NomadBreadcrumbs";
import NomadFileList, { mediaType, findAssociatedGps } from "./NomadFileList";
import NomadFolderList from "./NomadFolderList";

import "./nomadSelect.css";

export const contentDefinitionId = "3ff29f61-bd0b-4c17-b855-49db5a292aeb";
const startParentId = "9dd7145a-7968-4a29-94bb-4e1dc5d8f83c";
export const assetTypes = {
  folder: 1,
  file: 2,
};

function ensureUniqueName(name, existingNames, i = 0, originalName = null) {
  if (existingNames.indexOf(name) > -1) {
    const workingName = originalName ?? name;
    const left = workingName.substring(0, workingName.lastIndexOf("."));
    const right = workingName.substring(workingName.lastIndexOf("."));
    return ensureUniqueName(
      `${left}_${++i}${right}`,
      existingNames,
      i,
      workingName
    );
  }
  return name;
}

export default class NomadSelect extends React.Component {
  static contextType = CrashContext;
  constructor(props) {
    super(props);

    this.state = {
      mediaType: mediaType.video,
      multiSelect: true,
      searchValue: "", //search box text, as typing
      searchTerm: null, //accepted search text
      cancelButton: null,
    };

    this.props.setTitle(
      this.props.loadNomad
        ? "Loading Nomad Video, Please Wait..."
        : "Select Nomad Video(s)"
    );

    this.cancelFileSelectButton = (
      <button
        type="button"
        className="button light"
        onClick={this.cancelFileSelect.bind(this)}
      >
        Cancel
      </button>
    );

    this.navigateToFolder = this.navigateToFolder.bind(this);
    this.loadFiles = this.loadFiles.bind(this);
    this.searchValueChanged = this.searchValueChanged.bind(this);
    this.handleSearchKeyPress = this.handleSearchKeyPress.bind(this);
    this.performSearch = this.performSearch.bind(this);
    this.clearSearch = this.clearSearch.bind(this);
    this.setActionButton = this.setActionButton.bind(this);
  }

  async componentDidMount() {
    if (this.props.loadNomad) {
      this.setState({ folderId: startParentId });
      await this.loadVideo(this.props.loadNomad, true);
    } else await this.loadFolder(startParentId);
  }

  async componentDidUpdate() {
    if (
      this.props.loadNomad &&
      this.state.awaitingMapInitialisation &&
      this.context.mapInitialised
    ) {
      await this.loadVideo(this.props.loadNomad, true);
    }
  }

  async loadFolder(folderId) {
    this.setState({ loading: true });
    const token = await getCorpAuthToken(this.context.username);

    this.setState({
      currentToken: token,
      folderId,
      loading: false,
    });
  }

  async loadVideo(assetId, defaultRedirect = false) {
    if (!this.context.mapInitialised) {
      this.setState({ awaitingMapInitialisation: true });
      return;
    }

    this.setState({ loading: true, awaitingMapInitialisation: false });
    const token = await getCorpAuthToken(
      this.context.username,
      defaultRedirect
    );

    try {
      const response = await axios.get(
        `${this.props.nomadApiUrl}/api/asset/${assetId}/detail`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const detail = response && response.data;
      if (detail) {
        this.setState({ folderId: detail.properties.parentId });
        this.loadFiles([
          {
            video: {
              url: detail.properties.previewVideoFullUrl,
              name: detail.properties.displayName,
            },
            gps: await findAssociatedGps(detail),
          },
        ]);
      }
    } catch {
      messageState.message = ApplicationMessage.Error(
        "Unable to load requested video"
      );
      this.props.setTitle("Select Nomad Video(s)");
    }

    this.setState({
      currentToken: token,
      loading: false,
    });
  }

  async navigateToFolder(e) {
    e.stopPropagation();
    const folderId = e.currentTarget.dataset.id;
    this.setState({ searchTerm: null });
    await this.loadFolder(folderId);
  }

  // this function is called with an array of {video: {url, name}, gps: {url, name}} objects.
  // When mediaType is video, we expect 1+ objects with or without gps already set
  // When mediaType is text, expect 1 object with only gps set
  loadFiles(files) {
    if (this.state.mediaType === mediaType.video) {
      this.setState(
        {
          files,
          mediaType: mediaType.text,
          multiSelect: false,
        },
        this.checkForGPS
      );
    } else {
      if (files.length !== 1)
        throw new Error("Unexpected files selection: expecting one file");

      const updateIndex = this.state.files.findIndex((file) => !file.gps);
      const updatedFiles = [...this.state.files];
      const gps = files[0].gps;
      //can't have identical names for GPS files
      gps.name = ensureUniqueName(
        gps.name,
        this.state.files.filter((file) => file.gps).map((file) => file.gps.name)
      );
      updatedFiles[updateIndex] = { ...this.state.files[updateIndex], gps };
      this.setState({ files: updatedFiles }, this.checkForGPS);
    }
  }

  checkForGPS() {
    if (!this.state.files) return;

    const missingGpsFile = this.state.files.find((file) => !file.gps);
    if (missingGpsFile) {
      this.setState({ cancelButton: this.cancelFileSelectButton });
      this.props.setTitle(`Select GPS file for ${missingGpsFile.video.name}`);
    } else {
      this.props.filesSelected(this.state.files);
    }
  }

  searchValueChanged(ev) {
    this.setState({ searchValue: ev.target.value });
  }

  handleSearchKeyPress(ev) {
    if (ev.key === "Enter") this.performSearch();
  }

  performSearch() {
    this.setState({ searchTerm: this.state.searchValue });
  }

  clearSearch() {
    this.setState({ searchTerm: null });
  }

  setActionButton(actionButton) {
    this.props.setButtonOverrides({
      action: actionButton,
      cancel: this.state.cancelButton,
    });
  }

  cancelFileSelect() {
    const removeIndex = this.state.files.findIndex((file) => !file.gps);
    const files = [...this.state.files];
    files.splice(removeIndex, 1);
    this.setState({ files }, this.checkForGPS);
  }

  render() {
    return this.state.loading ? (
      <div className="nomadSelect">Loading...</div>
    ) : this.state.awaitingMapInitialisation ? (
      <div className="nomadSelect">Awaiting Map Initialisation...</div>
    ) : (
      <div className="nomadSelect">
        <div className="nomad-search">
          <div className="searchInput">
            <input
              type="search"
              id="search"
              name="search"
              placeholder="Search"
              value={this.state.searchValue}
              onChange={this.searchValueChanged}
              onKeyPress={this.handleSearchKeyPress}
            />
          </div>
          <div
            className="button searchButton"
            role="button"
            title="Perform Search"
            onClick={this.performSearch}
            onKeyPress={this.handleSearchKeyPress}
          >
            <FontAwesomeIcon icon={faSearch} size="lg" />
          </div>
        </div>
        <NomadBreadcrumbs
          folderId={this.state.folderId}
          searchTerm={this.state.searchTerm}
          clearSearch={this.clearSearch}
          token={this.state.currentToken}
          navigateToFolder={this.navigateToFolder}
          nomadApiUrl={this.props.nomadApiUrl}
        />
        <div className="nomad-navigate">
          {!this.state.searchTerm && (
            <NomadFolderList
              folderId={this.state.folderId}
              token={this.state.currentToken}
              navigateToFolder={this.navigateToFolder}
              nomadApiUrl={this.props.nomadApiUrl}
            />
          )}
          <NomadFileList
            folderId={this.state.folderId}
            token={this.state.currentToken}
            mediaType={this.state.mediaType}
            multiSelect={this.state.multiSelect}
            searchTerm={this.state.searchTerm}
            loadFiles={this.loadFiles}
            navigateToFolder={this.navigateToFolder}
            setActionButton={this.setActionButton}
            nomadApiUrl={this.props.nomadApiUrl}
          />
        </div>
      </div>
    );
  }
}
